<script setup>
    import Popper from 'vue3-popper';
    import { ref, reactive, onMounted, watch } from 'vue';

    const emits = defineEmits(['update:startDate', 'update:endDate', 'update:selectedCategories', 'update:isUsingNoEndDate', 'update:isValentineCampaign']);

    const props = defineProps({
        startDate: [Date, String, null],
        endDate: [Date, String, null],
        isValentineCampaign: Boolean,
        availableCategories: Array,
        selectedCategories: Array,
        editMode: Boolean,
        errors: Object,
    });

    const currentYear = new Date().getFullYear();

    // const valentineDatesRange = {
    //     min: new Date(currentYear, 1, 4),
    //     max: new Date(currentYear, 1, 14),
    // };

    const noEndDate = ref((props.startDate !== null && props.endDate === null) ? true : false);

    const iStartDate = ref(props.startDate);
    const iEndDate = ref(props.endDate);
    const valentine = ref(props.isValentineCampaign);
    const _selectedCategories = ref(props.selectedCategories);

    function sameDay(d1, d2) {
        return d1.getFullYear() === d2.getFullYear() &&
        d1.getMonth() === d2.getMonth() &&
        d1.getDate() === d2.getDate();
    }

    function disabledEndDates(date) {
        let date2 = new Date();

        if (props.startDate) {
            date2 = new Date(props.startDate);
        } else {
            date2.setDate(date2.getDate() -1);
        }

        // if (valentine.value) {
        //     return !(date >= valentineDatesRange.min && date <= valentineDatesRange.max && (sameDay(date, date2) || date >= date2));
        // }

        if (sameDay(date, date2)) {
            return false;
        }

        return date2 >= date;
    };

    function disabledStartDates(date) {
        // if (valentine.value) {
        //     return !(date >= valentineDatesRange.min && date <= valentineDatesRange.max);
        // }

        return false;
    };

    function updateStartDate(event) {
        emits('update:startDate', iStartDate.value);
    }

    function updateEndDate(event) {
        emits('update:endDate', iEndDate.value);
    }

    function isCategorySelected(category) {
        return _selectedCategories.value.includes(category.id);
    }

    function subcategoriesOf(category) {
        return props.availableCategories.filter(ca => ca.parent_id === category.id);
    }

    function selectCategory(category) {
        if (isCategorySelected(category)) {
            return;
        }

        _selectedCategories.value.push(category.id);

        if (category.parent_id === null) {
            subcategoriesOf(category).forEach((subcategory) => selectCategory(subcategory));
        }
    }

    function deselectCategory(category) {
        if (!isCategorySelected(category)) {
            return;
        }

        _selectedCategories.value.splice(_selectedCategories.value.indexOf(category.id), 1);

        if (category.parent_id === null) {
            subcategoriesOf(category).forEach((subcategory) => deselectCategory(subcategory));
        }
    }

    const valetineHover = ref(false);

    watch(noEndDate, (currentValue, oldValue) => {
        iEndDate.value = null;

        emits('update:endDate', null);

        if (currentValue) {
            emits('update:endDate', null);
            emits('update:isUsingNoEndDate', true);
        } else {
            emits('update:isUsingNoEndDate', false);
        }
    });

    watch(_selectedCategories, (currentValue, oldValue) => {
        emits('update:selectedCategories', _selectedCategories);
    });

    const iStartDatePickerKey = ref(0);
    const iEndDatePickerKey = ref(0);

    // watch(valentine, () => {
    //     iStartDate.value = null;
    //     iEndDate.value = null;

    //     emits('update:startDate', null);
    //     emits('update:endDate', null);
    //     emits('update:isValentineCampaign', valentine.value);

    //     iStartDatePickerKey.value = iStartDatePickerKey.value + 1;
    //     iEndDatePickerKey.value = iEndDatePickerKey.value + 1;
    // });
</script>

<template>
    <div :class="{ 'flex flex-col md:flex-row mb-6': editMode }">
        <div class="mb-4 md:mx-2" :class="{ 'md:w-1/3': editMode, 'mb-4 w-80 flex-none': !editMode }">
            <h2 class="text-2xl text-red mb-2">Välj datum och kategorier</h2>

            <p class="text-sm text-gray font-avertaLight">Här anger du mellan vilka datum din kampanj ska vara aktiv och synas live på familjekortet.se samt under vilka kategorier.</p>
        </div>

        <div class="md:mx-2 rounded-md" :class="{ 'md:w-2/3': editMode }">
            <div class="flex flex-col rounded-md border-2 border-blue-800 border-opacity-20 p-4">
                <!-- <div class="mb-4 p-2 rounded-md" style="background-color: #ed7d7d;" @mouseover="valetineHover = true" @mouseleave="valetineHover = false">
                    <div class="flex bg-white py-4 justify-center items-center rounded-md pl-6 heart-container" :class="{ 'hovered': valetineHover, 'selected': valentine }">
                        <input type="checkbox" class="heart__checkbox" v-model="valentine">

                        <div>
                            <div class="heart">
                                <div class="heart__icon"></div>
                            </div>
                        </div>

                        <div class="ml-6 text-sm font-avertaLight text-gray">
                            Alla hjärtans dag-kampanj? Klicka här och välj datum mellan 4e-14e februari, så kommer din kampanj att synas extra mycket på startsidan!
                        </div>
                    </div>
                </div> -->

                <div class="mb-4">
                    <label class="font-avertaBold text-sm text-gray">Startdatum</label>

                    <flatpickr
                        class="mt-2 rounded-md ring-0 outline-none border-2 border-blue focus:border-blue-600 py-2 px-4 w-full font-avertaLight text-sm disabled:bg-blue"
                        v-model="iStartDate"
                        @input="updateStartDate"
                        :key="iStartDatePickerKey"
                        :config="{
                            defaultDate: iStartDate,
                            disable: [disabledStartDates],
                        }"/>
                </div>

                <div class="mb-4">
                    <label class="inline-flex items-center font-avertaLight text-sm text-gray">
                        <span class="font-avertaBold">Slutdatum</span>

                        <Popper style="display: inline-flex;" :hover="true">
                            <icon name="info" class="ml-1"></icon>

                            <template #content>
                                <div class="popper-client-admin">Här anger du datumet när kampanjen ska göra sin sista dag. Kampanjen kommer ligga live på familjekortet till och med detta datum.</div>
                            </template>
                        </Popper>
                    </label>

                    <input v-if="noEndDate" type="text" value="Tillsvidare" class="mt-2 rounded-md ring-0 outline-none border-2 border-blue focus:border-blue-600 py-2 px-4 w-full font-avertaLight text-sm disabled:bg-blue" :disabled="true">

                    <flatpickr
                        v-show="!noEndDate"
                        class="mt-2 rounded-md ring-0 outline-none border-2 border-blue focus:border-blue-600 py-2 px-4 w-full font-avertaLight text-sm disabled:bg-blue"
                        :disabled="noEndDate"
                        v-model="iEndDate"
                        @input="updateEndDate"
                        :key="iEndDatePickerKey"
                        :config="{
                            clickOpens: !noEndDate,
                            defaultDate: iEndDate,
                            disable: [disabledEndDates],
                        }"/>

                    <label v-if="!valentine" class="mt-2 pl-1 flex items-center text-sm text-gray-700">
                        <input class="text-green-800 rounded-full" type="checkbox" v-model="noEndDate">
                        <span class="ml-2 font-avertaLight text-gray"><span class="font-avertaBold">Tillsvidare</span> - Kampanjen har inget slutdatum</span>
                    </label>
                </div>

                <label class="leading-loose inline-flex items-center font-avertaLight text-sm text-gray">
                    <span class="font-avertaBold">Kategorier</span>

                    <Popper style="display: inline-flex;" :hover="true">
                        <icon name="info" class="ml-1"></icon>

                        <template #content>
                            <div class="popper-client-admin">Välj vilken kategori erbjudandet tillhör. Om du önskar ha ett erbjudande aktivt i en kategori ska den kategorien ha en bock framför sig. Klicka ur bocken om erbjudandet ej gäller den kategorin.</div>
                        </template>
                    </Popper>
                </label>

                <div v-if="availableCategories.length" class="mt-1 space-y-3">
                    <div
                        v-for="category in availableCategories.filter(ca => ca.parent_id === null)"
                        class="border-2 border-blue rounded-md select-none relative text-gray"
                        :class="{ 'cursor-pointer': !isCategorySelected(category), 'bg-blue': isCategorySelected(category) }">
                        <div v-if="!isCategorySelected(category)" @click="selectCategory(category)" class="px-4 py-3 flex">
                            <span>{{ category.name }}</span>

                            <div class="h-[19px] w-[19px] border-2 border-green rounded-full self-center ml-auto"></div>
                        </div>

                        <div v-if="isCategorySelected(category)" class="flex px-4 py-3">
                            <div>
                                <span class="font-avertaLight">{{ category.name }}</span>

                                <div v-if="availableCategories.filter(ca => ca.parent_id === category.id).length" class="mt-1 space-y-1">
                                    <label v-for="subcategory in availableCategories.filter(ca => ca.parent_id === category.id)" class="flex items-center text-sm text-gray-700">
                                        <input class="text-green-800 rounded-full" type="checkbox" :value="subcategory.id" v-model="_selectedCategories">
                                        <span class="ml-2 font-avertaLight">{{ subcategory.name }}</span>
                                    </label>
                                </div>
                            </div>

                            <svg @click.prevent="deselectCategory(category)" xmlns="http://www.w3.org/2000/svg" class="self-center ml-auto text-green-800 h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
